/**
 * Auth Context for AMP Prediction Market
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { trackFunnelEvent } from '../services/funnelService';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      setError(null);
      // If the user returned from a Google OAuth handshake, fire auth_success
      try {
        if (userData && sessionStorage.getItem('amp_pending_google_auth')) {
          sessionStorage.removeItem('amp_pending_google_auth');
          trackFunnelEvent('auth_success', { source: 'login_google', userId: userData.user_id });
        }
      } catch (storageErr) {
        // sessionStorage unavailable (Safari private mode / WebKit) — not fatal.
        console.debug('sessionStorage read failed:', storageErr?.message);
      }
    } catch (err) {
      setUser(null);
      // Don't set error for normal "not authenticated" state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login with Google (redirect to Emergent auth)
  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try { sessionStorage.setItem('amp_pending_google_auth', '1'); } catch (storageErr) {
      // sessionStorage unavailable — funnel event will just be missed.
      console.debug('sessionStorage write failed:', storageErr?.message);
    }
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Login with email and password
  const loginWithEmail = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      // Clone response to safely read it
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store token
      if (data.access_token) {
        localStorage.setItem('session_token', data.access_token);
      }
      
      setUser(data.user);
      trackFunnelEvent('auth_success', { source: 'login_email', userId: data.user?.user_id });
      return { success: true, user: data.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Register with email and password
  const registerWithEmail = useCallback(async (email, password, username = null) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, username }),
      });

      // Read the body once, defensively. Some browsers / extensions / PWAs can
      // pre-consume Response bodies; fall back to a clone if that happens.
      let data = null;
      try {
        data = await response.json();
      } catch (readErr) {
        try {
          data = await response.clone().json();
        } catch {
          data = null;
        }
      }

      if (!response.ok) {
        const detail = (data && (data.detail || data.message)) || '';
        // Friendly mapping for the most common 400 from the register endpoint.
        const msg = /already\s*(registered|exists|in\s*use)/i.test(detail)
          ? 'Email already exists. Please sign in instead or use a different email.'
          : detail || 'Registration failed. Please try again.';
        throw new Error(msg);
      }

      if (data?.access_token) {
        localStorage.setItem('session_token', data.access_token);
      }

      setUser(data?.user);
      trackFunnelEvent('auth_success', { source: 'register_email', userId: data?.user?.user_id });
      return { success: true, user: data?.user };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Request password reset
  const requestPasswordReset = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }
      
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Reset password with token
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }
      
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Verify reset token
  const verifyResetToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid token');
      }
      
      return { success: true, email: data.email };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // Exchange session ID for user data (called after redirect)
  const exchangeSession = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      const userData = await apiService.exchangeSession(sessionId);
      setUser(userData);
      setError(null);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  }, []);

  // Refresh user data (e.g., after subscription)
  const refreshUser = useCallback(async () => {
    try {
      const userData = await apiService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      return null;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    // Admins always have full trading rights — bypass the subscription gate.
    isSubscribed: user?.is_subscribed || user?.is_admin || false,
    isAdmin: user?.is_admin || false,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    requestPasswordReset,
    resetPassword,
    verifyResetToken,
    exchangeSession,
    logout,
    refreshUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
